import {Box, Container, Flex, Text} from '@chakra-ui/react'
import {FC, useEffect, useState} from 'react'
import {useTranslation} from 'next-i18next'
import {Swiper, SwiperSlide} from 'swiper/react'
import dynamic from 'next/dynamic'
import {ProductJsonLd} from 'next-seo'
import {addApolloState, initializeApollo} from 'helpers/ApolloHelper/ApolloHelper'
import withAppInit from '_app/hoc/withAppInit'
import {Context} from '_app/types/context'
import Page from '_app/components/Page/Page'
import Gallery from 'modules/product/Gallery'
import ProductsCarousel from 'modules/Listing/ProductsCarousel'
import ProductInfo from 'modules/product/ProductInfo'
import Item from 'modules/Listing/Item'
import {BundleProduct, GetProductQuery, ProductInterface} from '_app/graphql/generated/graphql'
import {EngravingEntity} from '_app/graphql/cms/generated/graphql'
import {useRouter} from '_app/hooks/useRouter'
import {getProductImages} from 'modules/product/utils/gallery'
import {getEngravingsCMSAttributes} from 'modules/cart/components/engraving/utils/init'
import {Maybe} from 'graphql/jsutils/Maybe'
import SeoHelper from '../helpers/SeoHelper/SeoHelper'
import useProductAvailability from '../_app/hooks/useProductAvailability'
import {useForm} from 'react-hook-form'
import URLHelper from '../helpers/URLHelper/URLHelper'
import I18nHelper from '../helpers/I18nHelper/I18nHelper'
import StringHelper from '../helpers/StringHelper/StringHelper'
import {URL_RESOLVER} from '../_app/graphql/queries/URLResolver'
import {GetProductDocument} from '../_app/graphql/queries/products/GetProduct'
import BannerPromotion from '../components/molecules/BannerPromotion/BannerPromotion'

const LastViewedList = dynamic(() => import('modules/product/LastViewedList'))

interface ProductCardProps {
    product: BundleProduct | undefined;
    engravingsData: EngravingEntity[];
    hasNoIndex: boolean;
    hasNoFollow: boolean;
}

const ProductCard: FC<ProductCardProps> = ({
        product,
        engravingsData,
        hasNoIndex,
        hasNoFollow,
    }) => {
    const {t} = useTranslation('product')
    const router = useRouter()
    const { locale } = useRouter()
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const filteredMediaGallery = getProductImages(product)
    const [lookProducts, setLookProducts] = useState<{ id: any; }[]>([])
    const methods = useForm()
    const selectedOptions = methods
        .watch('selected_options')
        ?.flatMap((item: any) => item)
    const { availableProductAmount } = useProductAvailability({
        salable_qty: product?.salable_qty ?? 0,
        selectedOptions,
        itemsOptions: product?.items,
        product,
    })
    useEffect(() => {
        if (product?.related_products) {
            const temp: { id: any }[] = []
            product?.related_products?.forEach((el: Maybe<ProductInterface> | null) => {
                const id: any = el?.id
                if (id !== undefined && id !== product?.id) {
                    temp.push(el as { id: any })
                }
            })
            setLookProducts(temp)
        }
    }, [product?.related_products])
    const getTitle = (name: string) => {
        setTitle(name)
    }
    const getDescription = (name: string) => {
        let tempDescription = ''
        switch (locale) {
            case 'pl':
                tempDescription = `Produkt ${name} - wykonany z najwyższej jakości materiału, idealny na każdą okazję. Kup na prezent lub do codziennego noszenia - wejdź na Lilou.pl i ciesz się szybką dostawą!`
                break
            case 'en':
                tempDescription = `Product ${name} - Made of the highest quality material, perfect for any occasion. Buy for a gift or for everyday wear - enter Lilouparis.eu  and enjoy fast delivery!`
                break
            case 'fr':
                tempDescription = `Produits ${name} - fabriqués à partir de matériaux de la plus haute qualité, parfait pour toute les occasions. Achetez comme cadeau ou pour un usage quotidien - visitez Lilou.fr  et profitez d'une livraison rapide !`
                break
            case 'de':
                tempDescription = `Jetzt ${name} online bei Lilou Paris kaufen ✓ sofort lieferbar ✓ 14 Tage Rückgabe ✓`
                break
            default:
                tempDescription = `Produkt ${name} - wykonany z najwyższej jakości materiału, idealny na każdą okazję. Kup na prezent lub do codziennego noszenia - wejdź na Lilou.pl i ciesz się szybką dostawą!`
                break
        }
        setDescription(tempDescription)
    }
    useEffect(() => {
        if (typeof product?.name === 'string' && product?.name?.length > 0) {
            getTitle(product?.name)
            getDescription(product?.name)
        }
    }, [product?.name])
    return (
        <Page
            seo={{
                title: SeoHelper.getTitle(locale === 'de' ? `${product?.name} | Lilou Paris` : title),
                description: SeoHelper.getDescription(description),
                openGraph: {
                    images: filteredMediaGallery.map(({url}) => ({url})),
                },
            }}
            hasNoIndex={hasNoIndex}
            hasNoFollow={hasNoFollow}
        >
            <ProductJsonLd
                productName={product?.name || ''}
                sku={product?.sku || undefined}
                images={
                    filteredMediaGallery[0]?.url ? [filteredMediaGallery[0].url] : []
                }
                description={StringHelper.parseHTMLToString(product?.description?.html)}
                brand="lilou"
                offers={[
                    {
                        url: URLHelper.translateHref(router.pathname.split('?')[0], router.locale),
                        price: product?.price_range.minimum_price?.regular_price.value,
                        priceCurrency:
                        product?.price_range.minimum_price?.regular_price.currency,
                        availability: `https://schema.org/${availableProductAmount && availableProductAmount > 0 ? 'InStock' : 'OutOfStock'}`,
                    },
                ]}
            />
            <Container py={{base: 0, lg: 12}} px={{base: 0, lg: 4}}>
                <Flex flexDirection={{base: 'column', lg: 'row'}}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {filteredMediaGallery && (
                            <Gallery
                                badge={{
                                    image: product?.badge?.[0]?.label?.[0]?.imageUrl,
                                    title: product?.badge?.[0]?.label?.[0]?.title,
                                    categoryText: product?.badge?.[0]?.label?.[0]?.categoryText,
                                }}
                                items={filteredMediaGallery}
                                video={product?.vimeo_videos || ''}
                            />
                        )}
                        {
                            locale === 'pl' ? (
                                <BannerPromotion
                                    productView={true}
                                    link={'/pl/inspiracje/celebracja-kobiecosci.html'}
                                    background={'#f8b6c2'}
                                    startDate={'2025-03-07 00:01'}
                                    endDate={'2025-03-09 23:59'}
                                    title={'Celebruj Dzień Kobiet z Lilou. Do  końca akcji pozostało:'}
                                    subTitle={'Odbierz prezent do zakupów od 299 zł'}
                                />
                            ) : ''
                        }
                    </Box>
                    {product && (
                        <ProductInfo
                            product={product}
                            engravingsData={engravingsData}
                            mainImage={filteredMediaGallery[0]?.url}
                            px={{base: 4, lg: 0}}
                        />
                    )}
                </Flex>
            </Container>
            {product?.related_products && product?.related_products?.length > 0 && (
                <>
                    <Box
                        my={4}
                        display={{
                            base: 'block',
                            lg: 'none',
                        }}
                    >
                        <Text textStyle="sectionHeader" mb={4} mx={4}>
                            {t('OTHERS_FROM_COLLECTION')}
                        </Text>
                        <Container>
                            <Swiper slidesPerView={1.5} spaceBetween={30}>
                                {product?.related_products.map((item, index) => (
                                    <SwiperSlide key={`item-slide-[${index}]`}>
                                        <Item item={item as any}/>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </Container>
                    </Box>
                    <Box
                        mb={8}
                        display={{
                            base: 'none',
                            lg: 'block',
                        }}
                    >
                        <ProductsCarousel
                            title={t('OTHERS_FROM_COLLECTION')}
                            titleProps={{
                                textStyle: 'sectionHeader',
                            }}
                            items={lookProducts as any}
                            rows={1}
                        />
                    </Box>
                </>
            )}
            {product?.url_key && <LastViewedList url_key={product?.url_key}/>}
        </Page>
    )
}

export const getServerSideProps = withAppInit(async (ctx: Context) => {
    const url_key = decodeURIComponent(ctx?.params?.url_key)
    if (url_key === 'shop') {
        return {
            redirect: {
                destination: '/',
                permanent: false,
            },
        }
    }
    const apolloClient = initializeApollo(null, { locale: ctx.locale })
    const dataURL = await apolloClient.query({
        query: URL_RESOLVER,
        variables: {
            url: `${url_key}.html`,
        },
    })
    const url = dataURL?.data?.resolveCustomRedirect?.target_path
    const statusCode = Number(dataURL?.data?.resolveCustomRedirect?.redirect_type)
    if (url && (statusCode === 301 || statusCode === 302)) {
        return {
            redirect: {
                destination: `/${url}`,
                statusCode: statusCode || 301,
            },
        }
    }
    const [{data}, engravingsData] = await Promise.all([
        ctx.apolloClient.query<GetProductQuery>({
            query: GetProductDocument,
            variables: {
                search: '',
                filter: {url_key: {eq: url_key.replace('.html', '')}},
            },
        }),
        getEngravingsCMSAttributes(ctx.apolloClient, ctx.locale, new Date()),
    ])
    const product = data?.products?.items?.[0]
    if (!product) {
        return {
            notFound: true,
        }
    }

    return addApolloState(ctx.apolloClient, {
        props: {
            product,
            engravingsData,
            ...(await I18nHelper.getI18nProps(ctx, [
                'product',
                'engraving',
                'dropzone',
                'favorites',
            ])),
        },
    })
})

export default ProductCard
